import React, { useMemo } from 'react';
import { ROOF_TYPES, effectiveRoofArea, totalRoofArea } from '../calculations/roofCollectionCalculations.js';

/**
 * Étape 2 : surfaces de collecte (toiture ou surface assimilée) et leur coefficient de rendement.
 */
const RoofStep = ({ roofs, setRoofs, currentStep, totalSteps, goPrevious, goNext, canAdvance }) => {
  const totalArea = useMemo(() => totalRoofArea(roofs), [roofs]);
  const effectiveArea = useMemo(() => effectiveRoofArea(roofs), [roofs]);

  const updateRoof = (i, patch) => setRoofs(prev => prev.map((r, idx) => idx === i ? { ...r, ...patch } : r));

  const setRoofType = (i, type) => {
    setRoofs(prev => prev.map((r, idx) => idx === i ? { ...r, type, coef: ROOF_TYPES[type].coef } : r));
  };

  const removeRoof = (i) => setRoofs(prev => prev.filter((_, idx) => idx !== i));

  const addRoof = () => setRoofs(prev => [...prev, { id: `roof-${Date.now()}`, name: 'Nouvelle surface', area: 20, type: 'pente_rugueuse', coef: ROOF_TYPES.pente_rugueuse.coef }]);

  return (
    <div className="apv2-step-card">
      <div className="apv2-step-header">
        <div className="apv2-step-header-text">
          <span className="apv2-step-eyebrow">Récupération d'eau de pluie</span>
          <h2 className="apv2-step-heading">Surfaces de collecte</h2>
        </div>
        <span className="apv2-step-counter">{currentStep + 1} / {totalSteps}</span>
      </div>
      <div className="apv2-step-body">
        <div className="apv2-step-explanation">
          <span className="apv2-step-explanation-icon">💡</span>
          <p className="apv2-step-explanation-text">
            Ajoutez une ligne par pan de toiture (ou surface assimilée) raccordé à la récupération.
            Le coefficient de rendement traduit les pertes par évaporation, éclaboussures et premières eaux non collectées.
          </p>
        </div>

        {roofs.length === 0 && (
          <p className="apv2-sim-hint">Aucune surface renseignée — ajoutez au moins un pan de toiture.</p>
        )}

        {roofs.length > 0 && (
          <div className="apv2-flat-table-wrapper">
            <table className="apv2-flat-table">
              <thead>
                <tr>
                  <th>Surface</th>
                  <th>Type</th>
                  <th>Coefficient</th>
                  <th>Surface utile</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {roofs.map((r, i) => (
                  <tr key={r.id}>
                    <td>
                      <input type="text" className="rw-name-input" value={r.name} onChange={e => updateRoof(i, { name: e.target.value })} />
                      <input
                        type="number" min="0" step="1"
                        className="rw-vol-input"
                        value={r.area}
                        onChange={e => updateRoof(i, { area: +e.target.value || 0 })}
                      />
                      <span className="rw-unit">m²</span>
                    </td>
                    <td>
                      <select className="rw-profile-select" value={r.type} onChange={e => setRoofType(i, e.target.value)}>
                        {Object.entries(ROOF_TYPES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                      </select>
                    </td>
                    <td>
                      <input
                        type="number" min="0" max="1" step="0.01"
                        className="rw-vol-input"
                        value={r.coef}
                        disabled={r.type !== 'custom'}
                        onChange={e => updateRoof(i, { coef: +e.target.value || 0 })}
                      />
                    </td>
                    <td><span className="rw-coef-tag">{Math.round(r.area * r.coef)} m²</span></td>
                    <td className="apv2-flat-table-td-check">
                      <button type="button" className="rw-del-btn" title="Supprimer" onClick={() => removeRoof(i)}>✕</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <button type="button" className="rw-add-btn" onClick={addRoof}>+ Ajouter une surface</button>

        <p className="rw-coef-ref">
          Coefficients de référence — <b>Pente, surface lisse</b> 0,9 · <b>Pente, surface rugueuse</b> 0,8 ·
          <b> Toit plat sans gravier</b> 0,8 · <b>Toit plat avec gravier</b> 0,7 ·
          <b> Toiture végétalisée intensive</b> (ex. jardin) 0,3 · <b>Toiture végétalisée extensive</b> 0,5 ·
          <b> Zone étanche</b> (ex. asphalte) 0,8 · <b>Zone non étanche</b> (ex. galet) 0,5 ·
          <b> Personnalisé</b> (valeur libre).
        </p>

        <div className="apv2-summary-cards">
          <div className="apv2-summary-card">
            <span className="apv2-summary-value">{Math.round(totalArea).toLocaleString('fr-FR')} m²</span>
            <span className="apv2-summary-label">surface de toiture totale</span>
          </div>
          <div className="apv2-summary-card">
            <span className="apv2-summary-value">{Math.round(effectiveArea).toLocaleString('fr-FR')} m²</span>
            <span className="apv2-summary-label">surface de collecte utile (équiv.)</span>
          </div>
        </div>
      </div>
      <div className="apv2-step-footer">
        <button type="button" className="apv2-footer-btn-ghost" onClick={goPrevious} disabled={currentStep === 0}>
          ← Précédent
        </button>
        <button type="button" className="apv2-footer-btn-cta" onClick={goNext} disabled={!canAdvance}>
          Suivant →
        </button>
      </div>
    </div>
  );
};

export default RoofStep;
